import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import moment from 'moment-timezone'
import { IAssigneeOptions } from 'appHooks/useGetBusinessProfile'
import { BusinessUserType } from '__generated__/api-types-and-hooks'
import { getUserCalendarEventsByUserIdAction, setUserMeetingAction } from 'store/actions/calendar'
import { formatTimeString, addMinutesToTime, isValidEmail, isValidUrl } from '../helpers'

interface IUseMeetingHandlersProps {
  selectedGuests: IAssigneeOptions[]
  setSelectedGuests: (guests: IAssigneeOptions[]) => void
  guestSearchQuery: string
  setGuestSearchQuery: (query: string) => void
  setShowGuestDropdown: (show: boolean) => void
  setEmailError: (error: string) => void
  setHighlightedGuestIndex: (index: number) => void
  fetchedGuestsRef: React.MutableRefObject<Set<string>>
  pendingGuestRequests: React.MutableRefObject<Record<string, boolean>>
  setSelectedDate: (date: Date) => void
  setShowCalendar: (show: boolean) => void
  setShowStartTimePicker: (show: boolean) => void
  setShowEndTimePicker: (show: boolean) => void
  setStartTime: (time: string) => void
  setEndTime: (time: string) => void
  endTime: string
  setTemporarySlot: (slot: { date: Date; startHour: number; startMinute: number } | null) => void
  isCalendarClickRef: React.MutableRefObject<boolean>
  meetingLink: string
  setLinkCopied: (copied: boolean) => void
  filteredUsers: IAssigneeOptions[]
  highlightedGuestIndex: number
  meetingTitle: string
  selectedDate: Date | null
  startTime: string
  selectedTimezone: string
  currentUser: any
  tenantId: string
  isAdvisor: boolean
  meetingDescription: string
  meetingLocation: string
  onClose: () => void
  onSuccess?: () => void
  setShowCancelConfirmation: (show: boolean) => void
  setMeetingTitle: (title: string) => void
  setMeetingLink: (link: string) => void
  setMeetingDescription: (desc: string) => void
  setMeetingLocation: (location: string) => void
  assigneeOptions?: IAssigneeOptions[]
  validationErrors: {
    title: string
    date: string
    time: string
    guests: string
    meetingLink: string
  }
  setValidationErrors: (errors: {
    title: string
    date: string
    time: string
    guests: string
    meetingLink: string
  }) => void
}

export const useMeetingHandlers = ({
  selectedGuests,
  setSelectedGuests,
  guestSearchQuery,
  setGuestSearchQuery,
  setShowGuestDropdown,
  setEmailError,
  setHighlightedGuestIndex,
  fetchedGuestsRef,
  pendingGuestRequests,
  setSelectedDate,
  setShowCalendar,
  setShowStartTimePicker,
  setShowEndTimePicker,
  setStartTime,
  setEndTime,
  endTime,
  setTemporarySlot,
  isCalendarClickRef,
  meetingLink,
  setLinkCopied,
  filteredUsers,
  highlightedGuestIndex,
  meetingTitle,
  selectedDate,
  startTime,
  selectedTimezone,
  currentUser,
  tenantId,
  isAdvisor,
  meetingDescription,
  meetingLocation,
  onClose,
  onSuccess,
  setShowCancelConfirmation,
  setMeetingTitle,
  setMeetingLink,
  setMeetingDescription,
  setMeetingLocation,
  assigneeOptions,
  validationErrors,
  setValidationErrors,
}: IUseMeetingHandlersProps) => {
  const dispatch = useDispatch()

  const handleDateSelect = useCallback(
    (date: Date) => {
      setSelectedDate(date)
      setShowCalendar(false)
      setShowStartTimePicker(false)
      setShowEndTimePicker(false)
      // Clear date and time validation errors
      if (validationErrors.date || validationErrors.time) {
        setValidationErrors({ ...validationErrors, date: '', time: '' })
      }
    },
    [
      setSelectedDate,
      setShowCalendar,
      setShowStartTimePicker,
      setShowEndTimePicker,
      validationErrors,
      setValidationErrors,
    ]
  )

  const handleStartTimeSelect = useCallback(
    (time: string) => {
      setStartTime(time)
      setShowStartTimePicker(false)

      if (!endTime || endTime <= time) {
        const endTimeString = addMinutesToTime(time, 60)
        setEndTime(endTimeString)
      }
      // Clear time validation error
      if (validationErrors.time) {
        setValidationErrors({ ...validationErrors, time: '' })
      }
    },
    [
      setStartTime,
      setShowStartTimePicker,
      endTime,
      setEndTime,
      validationErrors,
      setValidationErrors,
    ]
  )

  const handleEndTimeSelect = useCallback(
    (time: string) => {
      setEndTime(time)
      setShowEndTimePicker(false)
      // Clear time validation error
      if (validationErrors.time) {
        setValidationErrors({ ...validationErrors, time: '' })
      }
    },
    [setEndTime, setShowEndTimePicker, validationErrors, setValidationErrors]
  )

  const handleGuestSelect = useCallback(
    (guest: IAssigneeOptions) => {
      setSelectedGuests([...selectedGuests, guest])
      setGuestSearchQuery('')
      setShowGuestDropdown(false)
      setEmailError('')
      setHighlightedGuestIndex(-1)
      // Clear guests validation error
      if (validationErrors.guests) {
        setValidationErrors({ ...validationErrors, guests: '' })
      }
    },
    [
      selectedGuests,
      setSelectedGuests,
      setGuestSearchQuery,
      setShowGuestDropdown,
      setEmailError,
      setHighlightedGuestIndex,
      validationErrors,
      setValidationErrors,
    ]
  )

  const handleRemoveGuest = useCallback(
    (guestValue: string) => {
      setSelectedGuests(selectedGuests.filter((guest) => guest.value !== guestValue))
      fetchedGuestsRef.current.delete(guestValue)

      if (pendingGuestRequests.current[guestValue]) {
        delete pendingGuestRequests.current[guestValue]
      }

      dispatch(getUserCalendarEventsByUserIdAction.RESET())
    },
    [selectedGuests, setSelectedGuests, fetchedGuestsRef, pendingGuestRequests, dispatch]
  )

  const handleAddGuestByEmail = useCallback(() => {
    const email = guestSearchQuery.trim().toLowerCase()

    if (!email) return

    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address')
      // Clear guest validation error to avoid showing both errors at once
      if (validationErrors.guests) {
        setValidationErrors({ ...validationErrors, guests: '' })
      }
      return
    }

    // Prevent adding the organizer's own email
    if (currentUser?.email?.toLowerCase() === email) {
      setEmailError('You cannot add yourself as a guest')
      // Clear guest validation error to avoid showing both errors at once
      if (validationErrors.guests) {
        setValidationErrors({ ...validationErrors, guests: '' })
      }
      return
    }

    if (
      selectedGuests.some((guest) => guest.value === email || guest.email?.toLowerCase() === email)
    ) {
      setEmailError('This email is already invited')
      // Clear guest validation error to avoid showing both errors at once
      if (validationErrors.guests) {
        setValidationErrors({ ...validationErrors, guests: '' })
      }
      return
    }

    // Check if this email belongs to an existing user in the system
    const existingUser = assigneeOptions?.find(
      (user) => user.email?.toLowerCase() === email || user.user?.email?.toLowerCase() === email
    )

    let guestToAdd: IAssigneeOptions

    if (existingUser) {
      // Use existing user's details with their name
      guestToAdd = {
        label: existingUser.label,
        value: existingUser.value,
        type: existingUser.type,
        email: existingUser.email,
        user: existingUser.user,
      }
    } else {
      // External guest - use email
      guestToAdd = {
        label: email,
        value: email,
        type: BusinessUserType.Member,
        email: email,
        user: {
          id: email,
          firstName: email.split('@')[0],
          lastName: '',
          email: email,
        },
      }
    }

    setSelectedGuests([...selectedGuests, guestToAdd])
    setGuestSearchQuery('')
    setShowGuestDropdown(false)
    setEmailError('')
  }, [
    guestSearchQuery,
    selectedGuests,
    setSelectedGuests,
    setGuestSearchQuery,
    setShowGuestDropdown,
    setEmailError,
    assigneeOptions,
    currentUser,
    validationErrors,
    setValidationErrors,
  ])

  const handleTimeSlotClick = useCallback(
    (date: Date, hour: number, startMinute: number) => {
      isCalendarClickRef.current = true
      setSelectedDate(new Date(date))

      setTemporarySlot({
        date: new Date(date),
        startHour: hour,
        startMinute: startMinute,
      })

      const startTimeString = formatTimeString(hour, startMinute)
      setStartTime(startTimeString)

      const endTimeString = addMinutesToTime(startTimeString, 15)
      setEndTime(endTimeString)
      // Clear date and time validation errors
      if (validationErrors.date || validationErrors.time) {
        setValidationErrors({ ...validationErrors, date: '', time: '' })
      }
    },
    [
      isCalendarClickRef,
      setSelectedDate,
      setTemporarySlot,
      setStartTime,
      setEndTime,
      validationErrors,
      setValidationErrors,
    ]
  )

  const handleCopyMeetingLink = useCallback(async () => {
    if (!meetingLink.trim()) return

    try {
      await navigator.clipboard.writeText(meetingLink)
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
    } catch (error) {
      // Failed to copy to clipboard
    }
  }, [meetingLink, setLinkCopied])

  const handleGuestInputKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        if (filteredUsers.length > 0 && highlightedGuestIndex >= 0) {
          handleGuestSelect(filteredUsers[highlightedGuestIndex])
        } else if (filteredUsers.length === 0) {
          handleAddGuestByEmail()
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedGuestIndex(
          highlightedGuestIndex < filteredUsers.length - 1 ? highlightedGuestIndex + 1 : 0
        )
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedGuestIndex(
          highlightedGuestIndex > 0 ? highlightedGuestIndex - 1 : filteredUsers.length - 1
        )
      } else if (e.key === 'Escape') {
        setShowGuestDropdown(false)
        setHighlightedGuestIndex(-1)
      }
    },
    [
      filteredUsers,
      highlightedGuestIndex,
      handleGuestSelect,
      handleAddGuestByEmail,
      setHighlightedGuestIndex,
      setShowGuestDropdown,
    ]
  )

  const handleSuggestedTimeClick = useCallback(
    (suggestion: { date: string; time: string; dateTime: Date }) => {
      setSelectedDate(suggestion.dateTime)

      const hours = suggestion.dateTime.getHours()
      const minutes = suggestion.dateTime.getMinutes()
      const startTimeString = formatTimeString(hours, minutes)
      setStartTime(startTimeString)

      const endDateTime = new Date(suggestion.dateTime)
      endDateTime.setMinutes(minutes + 30)
      const endTimeString = formatTimeString(endDateTime.getHours(), endDateTime.getMinutes())
      setEndTime(endTimeString)
      // Clear date and time validation errors
      if (validationErrors.date || validationErrors.time) {
        setValidationErrors({ ...validationErrors, date: '', time: '' })
      }
    },
    [setSelectedDate, setStartTime, setEndTime, validationErrors, setValidationErrors]
  )

  const handleCancelClick = useCallback(() => {
    const hasDescriptionContent =
      meetingDescription &&
      meetingDescription !== '<p></p>' &&
      meetingDescription.replace(/<[^>]*>/g, '').trim().length > 0

    const hasUnsavedData =
      meetingTitle ||
      selectedDate ||
      startTime ||
      endTime ||
      selectedGuests.length > 0 ||
      meetingLink ||
      meetingLocation ||
      hasDescriptionContent

    if (hasUnsavedData) {
      setShowCancelConfirmation(true)
    } else {
      onClose()
    }
  }, [
    meetingTitle,
    selectedDate,
    startTime,
    endTime,
    selectedGuests,
    meetingLink,
    meetingLocation,
    meetingDescription,
    setShowCancelConfirmation,
    onClose,
  ])

  const handleConfirmCancel = useCallback(() => {
    setShowCancelConfirmation(false)
    setMeetingTitle('')
    setMeetingLink('')
    setMeetingDescription('')
    setMeetingLocation('')
    setLinkCopied(false)
    onClose()
  }, [
    setShowCancelConfirmation,
    setMeetingTitle,
    setMeetingLink,
    setMeetingDescription,
    setMeetingLocation,
    setLinkCopied,
    onClose,
  ])

  const handleCancelConfirmation = useCallback(() => {
    setShowCancelConfirmation(false)
  }, [setShowCancelConfirmation])

  const validateMeetingForm = useCallback(() => {
    const errors = {
      title: '',
      date: '',
      time: '',
      guests: '',
      meetingLink: '',
    }
    let isValid = true

    // Validate title
    if (!meetingTitle.trim()) {
      errors.title = 'Please enter a meeting title'
      isValid = false
    }

    // Validate date
    if (!selectedDate) {
      errors.date = 'Please select a date'
      isValid = false
    }

    // Validate times
    if (!startTime || !endTime) {
      errors.time = 'Invalid time'
      isValid = false
    } else {
      // Check if end time is after start time
      const [startHour, startMin] = startTime.split(':').map(Number)
      const [endHour, endMin] = endTime.split(':').map(Number)
      const startMinutes = startHour * 60 + startMin
      const endMinutes = endHour * 60 + endMin

      if (endMinutes <= startMinutes) {
        errors.time = 'Invalid time'
        isValid = false
      }
    }

    // Validate guests
    if (selectedGuests.length === 0) {
      errors.guests = 'Please add at least one guest'
      isValid = false
      // Clear email error to avoid showing both errors at once
      setEmailError('')
    }

    // Validate meeting link
    if (!meetingLink.trim()) {
      errors.meetingLink = 'Meeting link is required'
      isValid = false
    } else if (!isValidUrl(meetingLink)) {
      errors.meetingLink = 'Invalid meeting URL format'
      isValid = false
    }

    setValidationErrors(errors)
    return isValid
  }, [
    meetingTitle,
    selectedDate,
    startTime,
    endTime,
    selectedGuests,
    meetingLink,
    setValidationErrors,
    setEmailError,
  ])

  const handleCreateMeeting = useCallback(() => {
    // Validate form before submission
    if (!validateMeetingForm()) {
      return
    }

    const startDateTime = moment(selectedDate)
      .set({
        hour: parseInt(startTime.split(':')[0]),
        minute: parseInt(startTime.split(':')[1]),
        second: 0,
        millisecond: 0,
      })
      .tz(selectedTimezone)
      .toISOString()

    const endDateTime = moment(selectedDate)
      .set({
        hour: parseInt(endTime.split(':')[0]),
        minute: parseInt(endTime.split(':')[1]),
        second: 0,
        millisecond: 0,
      })
      .tz(selectedTimezone)
      .toISOString()

    const attendees: string[] = []
    const teamMemberList: string[] = []

    selectedGuests.forEach((guest) => {
      const isExternal = guest.value.includes('@')
      if (isExternal) {
        attendees.push(guest.value)
      } else {
        teamMemberList.push(guest.value)
      }
    })

    // Ensure the current user (organizer) is always included in the team members list
    // UI excludes organizer from guest selection, but backend needs them for calendar queries
    if (!teamMemberList.includes(currentUser?.id || '')) {
      teamMemberList.push(currentUser?.id || '')
    }

    let meetingLocationTool = 'MICROSOFT_TEAMS'
    if (meetingLink) {
      if (meetingLink.includes('zoom')) {
        meetingLocationTool = 'ZOOM'
      } else if (meetingLink.includes('meet.google')) {
        meetingLocationTool = 'GOOGLE_MEET'
      } else if (!meetingLink.includes('teams.microsoft')) {
        meetingLocationTool = 'CUSTOM'
      }
    }

    const payload = {
      tenantId: tenantId || '',
      title: meetingTitle.trim(),
      description: meetingDescription || '',
      startTime: startDateTime,
      endTime: endDateTime,
      organizerName:
        `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() ||
        currentUser?.name ||
        '',
      advisorName: isAdvisor
        ? `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() ||
          currentUser?.name ||
          ''
        : undefined,
      location: meetingLocation || undefined,
      meetingLocationTool,
      videoLinkDescription: meetingLink || undefined,
      timezone: selectedTimezone,
      attendees: attendees.length > 0 ? attendees : undefined,
      teamMemberList: teamMemberList.length > 0 ? teamMemberList : undefined,
      role: currentUser?.roles || [],
      ignoreConflicts: false,
    }

    dispatch(setUserMeetingAction.STARTED(payload))
    onClose()
    onSuccess?.()
  }, [
    validateMeetingForm,
    meetingTitle,
    selectedDate,
    startTime,
    endTime,
    selectedGuests,
    selectedTimezone,
    currentUser,
    tenantId,
    isAdvisor,
    meetingDescription,
    meetingLocation,
    meetingLink,
    dispatch,
    onClose,
    onSuccess,
  ])

  return {
    handleDateSelect,
    handleStartTimeSelect,
    handleEndTimeSelect,
    handleGuestSelect,
    handleRemoveGuest,
    handleAddGuestByEmail,
    handleTimeSlotClick,
    handleCopyMeetingLink,
    handleGuestInputKeyPress,
    handleSuggestedTimeClick,
    handleCancelClick,
    handleConfirmCancel,
    handleCancelConfirmation,
    handleCreateMeeting,
  }
}
